/**
 * Outcomes Monitoring Report — the unified conduct/detriment monitoring layout.
 *
 * The framework (Consumer Duty vs TCF) is FIXED per report/route — there is no
 * in-page framework toggle. Consumer Duty and TCF
 * remain separate reports; the same component is mounted per framework.
 *
 * Level 1 (overview) and Level 2 (outcome detail) are one page; the level is
 * driven by the `outcome` URL param, alongside `grain` / `plot` / `bucket` so a
 * scoped drill-down is shareable (SPEC §7).
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Clock } from 'lucide-react';

import { ErrorBoundary } from '../../../ui/error-boundary';
import { Skeleton } from '../../../ui/skeleton';
import { Timestamp } from '../../../ui/timestamp';
import { FilterStrip, useFilterOptions } from '../../records/filter';
import { useReportFilterState } from '../useReportFilterState';
import { useDatasets } from '../../../../hooks/useDatasets';
import { AggregateRow, DetailView, OutcomeCard } from './components';
import { FRAMEWORK_CONFIG } from './constants';
import { useMonitoringOverview } from './hooks';
import type { Framework, Grain, Plot } from './types';
import { grainOptionsFor, periodDays, periodLabel } from './utils';

const VALID_PLOTS: Plot[] = ['score', 'detriment'];

function OutcomesMonitoringReportInner({ framework }: { framework: Framework }) {
  const { datasetId } = useParams<{ datasetId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { filters, setFilters, reset } = useReportFilterState();
  const { data: filterOptions, isLoading: optionsLoading } = useFilterOptions();
  const { data: datasetsData } = useDatasets();
  const currentDataset = useMemo(
    () => datasetsData?.datasets.find((d) => d.id === datasetId),
    [datasetsData, datasetId],
  );

  const config = FRAMEWORK_CONFIG[framework];
  const { data, isLoading, isError } = useMonitoringOverview(datasetId ?? '', framework, filters);

  // --- URL-driven Level-2 state ---------------------------------------------
  const grainOptions = useMemo(() => grainOptionsFor(periodDays(filters)), [filters]);
  const outcomeId = searchParams.get('outcome');
  const urlGrain = searchParams.get('grain') as Grain | null;
  const grain: Grain =
    urlGrain && grainOptions.allowed.includes(urlGrain) ? urlGrain : grainOptions.default;
  const urlPlot = searchParams.get('plot') as Plot | null;
  const plot: Plot = urlPlot && VALID_PLOTS.includes(urlPlot) ? urlPlot : 'score';
  const selectedBucket = searchParams.get('bucket');

  /** Update our own URL params while preserving filter/pagination params. */
  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null) next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openOutcome = useCallback(
    (id: string) => {
      // Change outcome resets bucket, plot, grain (SPEC §7); the open band is
      // local to BandTiles and remounts with the outcome.
      setParams({ outcome: id, grain: null, plot: null, bucket: null });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setParams],
  );
  const back = useCallback(
    () => setParams({ outcome: null, grain: null, plot: null, bucket: null }),
    [setParams],
  );
  const onGrainChange = useCallback(
    (g: Grain) => setParams({ grain: g, bucket: null }), // grain change clears selection
    [setParams],
  );
  const onPlotChange = useCallback((p: Plot) => setParams({ plot: p }), [setParams]);
  const onSelectBucket = useCallback(
    (b: string | null) => setParams({ bucket: b }),
    [setParams],
  );

  // Changing filters clears the scoped bucket (SPEC §7). Skip the first run.
  const filtersKey = JSON.stringify(filters);
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) {
      firstFilterRun.current = false;
      return;
    }
    if (searchParams.get('bucket')) setParams({ bucket: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const maxBand = useMemo(
    () => (data ? Math.max(1, ...data.outcomes.flatMap((o) => [o.bands.low, o.bands.mod, o.bands.high])) : 1),
    [data],
  );
  const sortedOutcomes = useMemo(
    () => (data ? [...data.outcomes].sort((a, b) => b.score - a.score) : []),
    [data],
  );

  if (!datasetId) return <div>Dataset ID is required</div>;

  const skeletonCount = framework === 'tcf' ? 6 : 5;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-[var(--go-spring)]">
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4 md:px-6 md:py-5 xl:px-8 xl:py-6">
        <div className="flex w-full flex-col gap-6">
          {/* breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/v4/records" className="font-medium text-primary hover:underline">
              Monitor
            </Link>
            <span>/</span>
            <Link to={`/v4/records/${datasetId}`} className="font-medium text-primary hover:underline">
              {currentDataset?.name ?? 'Collection'}
            </Link>
            <span>/</span>
            <span>{config.title}</span>
          </div>

          {/* header */}
          <section className="rounded-[28px] border border-border/70 bg-background/90 px-6 py-5 shadow-[var(--shadow-card)] backdrop-blur-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {config.eyebrow}
            </p>
            <h1 className="mt-2 font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground">
              {config.title}
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{config.standfirst}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {currentDataset?.lastActivity && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Last updated <Timestamp value={currentDataset.lastActivity} format="absolute" />
                </span>
              )}
              <span className="rounded-full border border-border px-2.5 py-0.5 font-bold">
                {config.jurisdiction}
              </span>
            </div>
          </section>

          {/* filter bar — framework is NOT a filter, so it stays out of this strip */}
          <FilterStrip
            filters={filters}
            onFiltersChange={setFilters}
            onReset={reset}
            isLoading={optionsLoading}
            filterOptions={filterOptions}
            className="rounded-[24px] border border-border/70 bg-background/90 p-4 shadow-[var(--shadow-card)] backdrop-blur-sm"
          />

          {isError ? (
            <div className="rounded-[var(--radius)] border border-border bg-background p-8 text-center text-destructive">
              Failed to load report data. Please try again.
            </div>
          ) : outcomeId ? (
            <DetailView
              datasetId={datasetId}
              framework={framework}
              filters={filters}
              outcomeId={outcomeId}
              grain={grain}
              allowedGrains={grainOptions.allowed}
              plot={plot}
              selectedBucket={selectedBucket}
              periodLabel={periodLabel(filters)}
              onGrainChange={onGrainChange}
              onPlotChange={onPlotChange}
              onSelectBucket={onSelectBucket}
              onBack={back}
            />
          ) : isLoading || !data ? (
            <div className="flex flex-col gap-6">
              <Skeleton className="h-32 w-full rounded-[var(--radius)]" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: skeletonCount }).map((_, i) => (
                  <Skeleton key={i} className="h-[22rem] w-full rounded-[var(--radius)]" />
                ))}
              </div>
            </div>
          ) : (
            <>
              <AggregateRow aggregate={data.aggregate} />

              <div>
                <h2 className="font-jakarta text-xl font-bold tracking-[-0.02em] text-foreground">
                  {config.outcomesHeading}
                </h2>
              </div>

              {sortedOutcomes.length ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {sortedOutcomes.map((o) => (
                    <OutcomeCard key={o.id} outcome={o} maxBand={maxBand} onOpen={openOutcome} />
                  ))}
                </div>
              ) : (
                <div className="rounded-[var(--radius)] border border-border bg-background p-8 text-center text-muted-foreground">
                  No outcome data found for the selected filters
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function OutcomesMonitoringReportPage({ framework = 'cd' }: { framework?: Framework }) {
  return (
    <ErrorBoundary>
      <OutcomesMonitoringReportInner framework={framework} />
    </ErrorBoundary>
  );
}
